import type { FC } from 'react'
import React, { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useCreate } from '@/api/kubik/credit'
import type { FundingRequestData } from '@/api/kubik/monolith'
import { FinancingProductError } from '@/components/composed/financing/FinancingProductError'
import { addModalToWellKnown } from '@/components/composed/Modal/constants'
import { FinancingConsultingModal } from '@/components/composed/modals'
import { pushGtmEventOnSubmitOrder } from '@/logic/metrika/financing/gtmHelpers'
import { EGtmBlock } from '@/logic/metrika/financing/types'
import { getUserGtmInfo } from '@/logic/metrika/helpers'
import { getUserRegion } from '@/logic/userRegion'
import { ApplyingFinancingOfferSuccessModal } from '@/pages/Financing/components'
import { LoanProductConsult } from '@/pages/Financing/components/FinancingProducts/components/LoanProducts/components/LoanProductsList/components/LoanProduct/components/LoanProductCollapsing/LoanProductConsult'
import { LoanProductOrderMethodCommunication } from '@/pages/Financing/components/FinancingProducts/components/LoanProducts/components/LoanProductsList/components/LoanProduct/components/LoanProductCollapsing/LoanProductOrderMethodCommunication'
import { PublicationFinancingWidgetSuccessOrder } from '@/pages/JournalPublication/components/PublicationText/components/PublicationFinancingWidget/components/PublicationFinancingWidgetCollapsingFinancingProductOrder/components/PublicationFinancingWidgetSuccessOrder'
import type { RootState } from '@/redux/interfaces'
import type { EFinancingProductId } from '@/types'
import { EUserProfileRoute } from '@/types'
import { createRouterLink } from '@/utils/url'
import { PublicationFinancingWidgetFinancingOrder } from './components/PublicationFinancingWidgetFinancingOrder'
import { EActiveBlock } from './constants'
import type { IPublicationFinancingWidgetCollapsingProps } from './types'
import './styles.scss'

addModalToWellKnown({ FinancingConsultingModal, ApplyingFinancingOfferSuccessModal })

const mapState = (state: RootState) => ({
  userGtmInfo: getUserGtmInfo()(state),
  userRegion: getUserRegion()(state),
})

export const PublicationFinancingWidgetCollapsingFinancingProductOrder: FC<IPublicationFinancingWidgetCollapsingProps> = props => {
  const { userGtmInfo, userRegion } = useSelector(mapState)
  const { widgetType, financingProductId, financingOfferType } = props

  const history = useHistory()

  const [activeBlock, setActiveBlock] = useState<EActiveBlock>(EActiveBlock.FINANCING_ORDER)
  const [successFundingRequestId, setSuccessFundingRequestId] = useState<number | undefined>()

  const setDefaultStep = useCallback(() => setActiveBlock(EActiveBlock.FINANCING_ORDER), [])
  const setSuccessStep = useCallback(() => setActiveBlock(EActiveBlock.SUCCESS_FINANCING_OFFER), [])
  const setErrorStep = useCallback(() => setActiveBlock(EActiveBlock.ERROR), [])

  const { mutateAsync: createFundingRequest, isLoading } = useCreate({
    mutation: {
      onSuccess: (data, variables) => {
        setSuccessFundingRequestId(data.data.fundingRequests?.[0]?.id)
        setActiveBlock(EActiveBlock.COMMUNICATION_METHOD)
        const fundingRequests = data.data.fundingRequests ?? []
        const productIds = variables.productIds as EFinancingProductId[]

        pushGtmEventOnSubmitOrder(
          userGtmInfo,
          userRegion.name,
          fundingRequests as Pick<FundingRequestData, 'id' | 'multiId'>[],
          productIds,
          EGtmBlock.FORM,
        )
      },
      onError: () => setActiveBlock(EActiveBlock.ERROR),
    },
  })

  const onClickToOrderPage = useCallback(() => {
    history.push(createRouterLink(EUserProfileRoute.fundingRequest, { id: String(successFundingRequestId) }))
  }, [history, successFundingRequestId])

  const renderActiveBlock = () => {
    switch (activeBlock) {
      case EActiveBlock.COMMUNICATION_METHOD:
        if (successFundingRequestId) {
          return (
            <LoanProductOrderMethodCommunication
              fundingRequestId={successFundingRequestId}
              onSuccessUpdateFundingRequest={setSuccessStep}
              onErrorUpdateFundingRequest={setErrorStep}
            />
          )
        }

        return null
      case EActiveBlock.SUCCESS_FINANCING_OFFER:
        return <PublicationFinancingWidgetSuccessOrder onCloseSuccessMessage={setDefaultStep} onToOrder={onClickToOrderPage} />
      case EActiveBlock.ERROR:
        return <FinancingProductError onResetStep={setDefaultStep} />
      case EActiveBlock.CONSULTATION:
        return (
          <LoanProductConsult
            onBack={setDefaultStep}
            product={{ productId: financingProductId, title: '', additionalCards: [], detailCards: [] }}
          />
        )
      case EActiveBlock.FINANCING_ORDER:
      default:
        return (
          <PublicationFinancingWidgetFinancingOrder
            widgetType={widgetType}
            setIsActiveBlock={setActiveBlock}
            isLoading={isLoading}
            createFundingRequest={createFundingRequest}
            financingProductId={financingProductId}
            financingOfferType={financingOfferType}
            setSuccessFundingRequestId={setSuccessFundingRequestId}
          />
        )
    }
  }

  return <div className="publicationFinancingWidgetCollapsing">{renderActiveBlock()}</div>
}
